// js/nav.js — Navigation bar builder. Accepts a re-render callback so it
// doesn't need to import from app.js (avoids circular deps).

import * as Cal from './calendar.js';

/**
 * Renders the period navigation bar into `container`.
 * `onRerender` is called whenever the user changes the date or view.
 */
export function buildNavBar(container, onRerender) {
  const range = Cal.getCurrentRange();
  container.innerHTML = `
    <div class="flex items-center gap-3 mb-4 flex-wrap">
      <button id="nav-prev"  class="px-3 py-1.5 rounded border border-gray-300 text-sm hover:bg-gray-100">← Prev</button>
      <span   id="nav-label" class="font-semibold text-gray-700 min-w-[220px] text-center">${range.label}</span>
      <button id="nav-next"  class="px-3 py-1.5 rounded border border-gray-300 text-sm hover:bg-gray-100">Next →</button>
      <button id="nav-today" class="px-3 py-1.5 rounded text-sm ml-1 nav-today-btn">Today</button>
      <div class="flex gap-1 ml-4">
        ${['day','week','month','year'].map(v => `
          <button data-view="${v}" class="view-btn px-3 py-1.5 rounded text-sm border ${Cal.getView() === v ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 hover:bg-gray-100'}">${v.charAt(0).toUpperCase() + v.slice(1)}</button>
        `).join('')}
      </div>
      <div style="position:relative;" class="ml-2">
        <button id="nav-help" title="Keyboard shortcuts"
          class="px-3 py-1.5 rounded text-sm border border-gray-300 hover:bg-gray-100">?</button>
        <div id="nav-help-panel" style="display:none;position:absolute;right:0;top:calc(100% + 4px);background:#fff;border:1px solid #E4E3E0;border-radius:8px;padding:8px 12px;box-shadow:0 4px 16px rgba(0,0,0,0.10);z-index:100;">
          <div style="font-size:0.78rem;display:flex;flex-direction:column;gap:3px;">
            <div style="display:flex;align-items:center;gap:12px;"><span style="font-family:monospace;font-weight:600;color:#1e293b;min-width:54px;">n</span><span style="color:#374151;white-space:nowrap;">Add entry</span></div>
            <div style="display:flex;align-items:center;gap:12px;"><span style="font-family:monospace;font-weight:600;color:#1e293b;min-width:54px;">← →</span><span style="color:#374151;white-space:nowrap;">Navigate periods</span></div>
            <div style="display:flex;align-items:center;gap:12px;"><span style="font-family:monospace;font-weight:600;color:#1e293b;min-width:54px;">t</span><span style="color:#374151;white-space:nowrap;">Go to today</span></div>
            <div style="display:flex;align-items:center;gap:12px;"><span style="font-family:monospace;font-weight:600;color:#1e293b;min-width:54px;">d w m y</span><span style="color:#374151;white-space:nowrap;">Switch view</span></div>
            <div style="display:flex;align-items:center;gap:12px;"><span style="font-family:monospace;font-weight:600;color:#1e293b;min-width:54px;">Esc</span><span style="color:#374151;white-space:nowrap;">Cancel edit</span></div>
          </div>
        </div>
      </div>
    </div>
  `;

  container.querySelector('#nav-prev').addEventListener('click', () => {
    Cal.goPrev(); onRerender();
  });
  container.querySelector('#nav-next').addEventListener('click', () => {
    Cal.goNext(); onRerender();
  });
  container.querySelector('#nav-today').addEventListener('click', () => {
    Cal.setDate(new Date()); onRerender();
  });
  container.querySelectorAll('[data-view]').forEach(btn => {
    btn.addEventListener('click', () => {
      Cal.setView(btn.dataset.view); onRerender();
    });
  });

  // ── Help dropdown ────────────────────────────────────────────────────────────
  const helpBtn   = container.querySelector('#nav-help');
  const helpPanel = container.querySelector('#nav-help-panel');
  let _dismiss = null;

  const removeDismiss = () => {
    if (_dismiss) {
      document.removeEventListener('mousedown', _dismiss);
      document.removeEventListener('touchstart', _dismiss);
      _dismiss = null;
    }
  };

  helpBtn.addEventListener('click', e => {
    e.stopPropagation();
    const isOpen = helpPanel.style.display !== 'none';
    if (isOpen) {
      helpPanel.style.display = 'none';
      removeDismiss();
    } else {
      helpPanel.style.display = 'block';
      _dismiss = ev => {
        if (!helpPanel.contains(ev.target) && ev.target !== helpBtn) {
          helpPanel.style.display = 'none';
          removeDismiss();
        }
      };
      document.addEventListener('mousedown', _dismiss);
      document.addEventListener('touchstart', _dismiss);
    }
  });
}
